import React from 'react';
import { smoothScrollTo } from '../hooks/useGSAPAnimations';
import { HugeiconsIcon } from '@hugeicons/react';
import { GolfCartIcon, Award01Icon, Download01Icon, Target01Icon, Flag02Icon, AiBrain02Icon, GolfBatIcon, ArrowDown01Icon } from '@hugeicons/core-free-icons';

const Hero = () => {
  const scrollToSection = (sectionId) => {
    smoothScrollTo(sectionId);
  };

  return (
    <section className="hero-gradient text-white pt-8 pb-12 px-4 sm:pt-12 sm:pb-20 lg:pt-16 lg:pb-20 relative overflow-hidden">
      {/* Golf Grass Texture as subtle background - Optimized */}
      <div className="absolute inset-0 opacity-40">
        <img
          src="https://res.cloudinary.com/dzcrxivpm/image/upload/c_fill,g_auto,h_675,w_400/dpr_auto/f_auto/q_auto/v1758805052/openart-image_w1hygJNz_1758804973938_raw_pi6al7.webp"
          srcSet="https://res.cloudinary.com/dzcrxivpm/image/upload/c_fill,g_auto,h_675,w_400/dpr_auto/f_auto/q_auto/v1758805052/openart-image_w1hygJNz_1758804973938_raw_pi6al7.webp 400w,
                  https://res.cloudinary.com/dzcrxivpm/image/upload/c_fill,g_auto,h_675,w_800/dpr_auto/f_auto/q_auto/v1758805052/openart-image_w1hygJNz_1758804973938_raw_pi6al7.webp 800w,
                  https://res.cloudinary.com/dzcrxivpm/image/upload/c_fill,g_auto,h_675,w_1200/dpr_auto/f_auto/q_auto/v1758805052/openart-image_w1hygJNz_1758804973938_raw_pi6al7.webp 1200w"
          sizes="100vw"
          alt="Golf grass texture"
          width="1200"
          height="675"
          fetchpriority="high"
          decoding="sync"
          className="w-full h-full object-cover mix-blend-multiply"
        />
      </div>

      {/* Gradient Overlay to blend grass */}
      <div className="absolute inset-0 bg-gradient-to-b from-green-800/70 via-green-700/80 to-green-900/85"></div>

      {/* Enhanced Background Pattern */}
      <div className="absolute inset-0 hero-background-elements">
        <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-white/5 rounded-full blur-2xl"></div>
        <div className="absolute bottom-20 left-32 w-20 h-20 border border-white/10 rounded-full"></div>
        <div className="absolute top-1/2 right-1/3 w-40 h-40 bg-gradient-to-br from-white/10 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">

          {/* Left Column - Copy */}
          <div className="text-center lg:text-left hero-content">
            {/* Pre-headline with enhanced styling */}
            <div className="premium-badge inline-flex mb-6 pulse-glow">
              <HugeiconsIcon icon={GolfCartIcon} size={18} />
              CADDIE SECRETS
              <HugeiconsIcon icon={Award01Icon} size={18} />
            </div>

            {/* Main Headline with responsive typography */}
            <h1 className="font-playfair font-bold text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight sm:leading-relaxed mb-4 sm:mb-6 tracking-tight">
              Finally: The Caddie Secrets Amateurs
              <span className="text-yellow-400"> Pay $200+ per Round For!</span>
            </h1>

            {/* Subheadline */}
            <h2 className="text-base sm:text-xl lg:text-2xl mb-4 sm:mb-6 text-gray-100 leading-snug sm:leading-relaxed font-medium">
              Battle-tested course management from 10+ years on the bag.
              <strong className="text-yellow-400"> No swing changes required.</strong>
            </h2>

            {/* Book Image - Mobile Only (between headline and benefits) */}
            <div className="lg:hidden my-6 flex flex-col items-center">
              <div className="w-full flex justify-center py-12">
                <img
                  src="https://res.cloudinary.com/dzcrxivpm/image/upload/w_800,f_auto,q_auto/v1758745884/glac_mockup_yqpv6v.webp"
                  srcSet="
                    https://res.cloudinary.com/dzcrxivpm/image/upload/w_400,f_auto,q_auto/v1758745884/glac_mockup_yqpv6v.webp 400w,
                    https://res.cloudinary.com/dzcrxivpm/image/upload/w_600,f_auto,q_auto/v1758745884/glac_mockup_yqpv6v.webp 600w,
                    https://res.cloudinary.com/dzcrxivpm/image/upload/w_800,f_auto,q_auto/v1758745884/glac_mockup_yqpv6v.webp 800w
                  "
                  sizes="(max-width: 640px) 210px, 280px"
                  alt="Golf Like a Caddie Volume 1 - E-book Cover"
                  width="800"
                  height="1067"
                  fetchpriority="high"
                  className="w-48 sm:w-64 h-auto object-contain drop-shadow-2xl scale-110"
                  style={{ transform: 'translateX(35px) scale(1.5)' }}
                />
              </div>
              {/* Primary CTA - Mobile Only */}
              <button
                onClick={() => scrollToSection('#form')}
                className="w-full inline-flex items-center justify-center gap-2 cta-button text-white font-inter font-bold text-base px-6 py-3 min-h-[44px] rounded-lg shadow-luxury text-center cursor-pointer mb-6"
              >
                <HugeiconsIcon icon={Download01Icon} size={20} />
                <span>Get Your Free Guide Now</span>
              </button>

              {/* Social Proof - Mobile Only */}
              <div className="flex items-center justify-center">
                <div className="flex -space-x-2">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-3 border-white shadow-lg bg-white p-0.5">
                    <img
                      src="https://images.pexels.com/photos/6256890/pexels-photo-6256890.jpeg?w=150&h=150&fit=crop&crop=faces&auto=format"
                      alt="Male golfer"
                      width="150"
                      height="150"
                      className="w-full h-full rounded-full object-cover"
                    />
                  </div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-3 border-white shadow-lg bg-white p-0.5">
                    <img
                      src="https://images.pexels.com/photos/9207390/pexels-photo-9207390.jpeg?w=150&h=150&fit=crop&crop=faces&auto=format"
                      alt="Female golfer"
                      width="150"
                      height="150"
                      className="w-full h-full rounded-full object-cover"
                    />
                  </div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-3 border-white shadow-lg bg-white p-0.5">
                    <img
                      src="https://images.pexels.com/photos/1637731/pexels-photo-1637731.jpeg?w=150&h=150&fit=crop&crop=faces&auto=format"
                      alt="Male golfer"
                      width="150"
                      height="150"
                      className="w-full h-full rounded-full object-cover"
                    />
                  </div>
                </div>
                <p className="ml-3 text-xs sm:text-sm text-gray-200">Join 1000+ golfers already<br/>using these caddie secrets!</p>
              </div>
            </div>

            {/* Key Benefits with glass effect */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-6 sm:mb-8 text-left">
              <div className="flex items-center space-x-3 dark-glass-card p-2 sm:p-3 rounded-lg">
                <div className="w-7 h-7 sm:w-9 sm:h-9 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                  <HugeiconsIcon icon={Target01Icon} size={16} color="white" />
                </div>
                <span className="text-gray-100 font-medium text-sm sm:text-base">Save up to 10+ strokes per round!</span>
              </div>
              <div className="flex items-center space-x-3 dark-glass-card p-2 sm:p-3 rounded-lg">
                <div className="w-7 h-7 sm:w-9 sm:h-9 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                  <HugeiconsIcon icon={Flag02Icon} size={16} color="white" />
                </div>
                <span className="text-gray-100 font-medium text-sm sm:text-base">Hit more greens</span>
              </div>
              <div className="flex items-center space-x-3 dark-glass-card p-2 sm:p-3 rounded-lg">
                <div className="w-7 h-7 sm:w-9 sm:h-9 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                  <HugeiconsIcon icon={AiBrain02Icon} size={16} color="white" />
                </div>
                <span className="text-gray-100 font-medium text-sm sm:text-base">Play your best golf</span>
              </div>
              <div className="flex items-center space-x-3 dark-glass-card p-2 sm:p-3 rounded-lg">
                <div className="w-7 h-7 sm:w-9 sm:h-9 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                  <HugeiconsIcon icon={GolfBatIcon} size={16} color="white" />
                </div>
                <span className="text-gray-100 font-medium text-sm sm:text-base">No swing change required</span>
              </div>
            </div>

            {/* Social Proof - Desktop/Tablet Only */}
            <div className="hidden sm:flex items-center justify-center lg:justify-start mb-6">
              <div className="flex -space-x-2">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-3 border-white shadow-lg bg-white p-0.5">
                  <img
                    src="https://images.pexels.com/photos/6256890/pexels-photo-6256890.jpeg?w=150&h=150&fit=crop&crop=faces&auto=format"
                    alt="Male golfer"
                    width="150"
                    height="150"
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-3 border-white shadow-lg bg-white p-0.5">
                  <img
                    src="https://images.pexels.com/photos/9207390/pexels-photo-9207390.jpeg?w=150&h=150&fit=crop&crop=faces&auto=format"
                    alt="Female golfer"
                    width="150"
                    height="150"
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-3 border-white shadow-lg bg-white p-0.5">
                  <img
                    src="https://images.pexels.com/photos/1637731/pexels-photo-1637731.jpeg?w=150&h=150&fit=crop&crop=faces&auto=format"
                    alt="Male golfer"
                    width="150"
                    height="150"
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
              </div>
              <p className="ml-4 text-sm sm:text-base text-gray-200">Join 1000+ golfers<br/>already using these caddie secrets!</p>
            </div>

            {/* Enhanced CTA Buttons - Hidden on mobile, visible on tablet+ */}
            <div className="hidden sm:flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => scrollToSection('#form')}
                className="inline-flex items-center justify-center gap-2 cta-button text-white font-inter font-bold text-base sm:text-lg px-6 py-3 min-h-[44px] rounded-lg shadow-luxury text-center cursor-pointer"
              >
                <HugeiconsIcon icon={Download01Icon} size={20} />
                <span>Get Your Free Guide Now</span>
              </button>
              <button
                onClick={() => scrollToSection('#learn')}
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white font-inter font-semibold text-base sm:text-lg px-6 py-3 min-h-[44px] rounded-lg hover:bg-white/20 hover:border-yellow-300 transition-all text-center cursor-pointer"
              >
                <span>Learn More</span>
                <HugeiconsIcon icon={ArrowDown01Icon} size={20} />
              </button>
            </div>
          </div>

          {/* Right Column - Book Mockup (Desktop Only) */}
          <div className="hidden lg:flex lg:items-center lg:justify-center relative">
            <img
              src="https://res.cloudinary.com/dzcrxivpm/image/upload/w_800,f_auto,q_auto/v1758745884/glac_mockup_yqpv6v.webp"
              srcSet="
                https://res.cloudinary.com/dzcrxivpm/image/upload/w_400,f_auto,q_auto/v1758745884/glac_mockup_yqpv6v.webp 400w,
                https://res.cloudinary.com/dzcrxivpm/image/upload/w_600,f_auto,q_auto/v1758745884/glac_mockup_yqpv6v.webp 600w,
                https://res.cloudinary.com/dzcrxivpm/image/upload/w_800,f_auto,q_auto/v1758745884/glac_mockup_yqpv6v.webp 800w,
                https://res.cloudinary.com/dzcrxivpm/image/upload/w_1200,f_auto,q_auto/v1758745884/glac_mockup_yqpv6v.webp 1200w
              "
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 800px"
              alt="Golf Like a Caddie Volume 1 - E-book Cover"
              width="800"
              height="1067"
              fetchpriority="high"
              className="w-full h-auto object-contain drop-shadow-2xl lg:scale-125 lg:translate-x-60"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;